import React, { useEffect, useMemo, useState } from "react";
import { Tag } from "antd";
import { ImageCheck, LinkWrapper } from "Explore/Components";
import { ImageCheckType } from "Explore/Config/ImageType";
import styles from "./FacultyCard.module.scss";
import { IconPack } from "Explore/Config/Icons";
import { numberRegex } from "Explore/Utils/numberRegex";
import { Button, Grid, IconButton, Typography } from "@mui/material";
import { LinkOutlined } from "@mui/icons-material";
import classNames from "classnames";
import FacultyCardBasicInfo from "./BasicInfo";
import _ from "lodash";
import FavoriteButton from "../FavoriteButton";

const hasFtsHtml = (str) => typeof str === "string" && str.includes("<");

const Card = ({ data: _data, className, simpleMode, onKeywordClick, hideActions }) => {
  const data = {
    ..._data,
    keyword_specific_stats: _.omitBy(
      _data.keyword_specific_stats,
      (value) => _.isNil(value) || _.isEmpty(value)
    ),
  };

  const [seeAll, setSeeAll] = useState(false);
  const [useSeeAll, setUseSeeAll] = useState(false);

  useEffect(() => {
    if (data.experience_metadata && data.experience_metadata.length > 8) {
      setUseSeeAll(true);
    }
  }, [data.experience_metadata]);

  // Pick the first snippet that isn't the email, so it doesn't duplicate the
  // Email field shown above.
  const snippetKey = useMemo(
    () => Object.keys(data.snippet || {}).find((key) => key !== "email"),
    [data.snippet]
  );

  const statistics = [
    {
      icon: IconPack.publications,
      value: data.publication_count,
      title: "Publications",
    },
    {
      icon: IconPack.citation,
      value: data.citation_count,
      title: "Citations",
    },
    {
      icon: IconPack.h_index,
      value: data.h_index,
      title: "h-index",
    },
    {
      icon: IconPack.seniority,
      value: data.seniority,
      title: "Academic ages",
    },
  ].filter((e) => !!e.value);

  const renderAvatar = () => {
    return (
      <LinkWrapper
        link={`/explore/faculty/${data.explore_slug || data.id}`}
        component={
          <ImageCheck
            url={data.photo}
            className={styles["avatar"]}
            alt={"avatar"}
            type={ImageCheckType.FACULTY_AVATAR}
          />
        }
      />
    );
  };

  return (
    <>
      <div className={classNames(styles["faculty-card"], className)}>
        <div className={styles["faculty-card__top"]}>
          {renderAvatar()}
          <div className={styles["faculty-name--wrapper"]}>
            <LinkWrapper
              link={`/explore/faculty/${data.explore_slug || data.id}`}
              component={
                <div
                  className={classNames(styles["faculty-name"], styles["link"])}
                >
                  {typeof data.name === "string" &&
                  data.name.includes("<") ? (
                    <p
                      dangerouslySetInnerHTML={{ __html: data.name }}
                    />
                  ) : (
                    <p>{data.name}</p>
                  )}
                </div>
              }
            />
            {!hideActions && (
              <div className={styles["entity-actions"]}>
                <FavoriteButton
                  isFavorited={data.is_favorited}
                  itemId={data.id}
                  itemType="faculty"
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles["faculty-card__body"]}>
          <div className={styles["faculty-card__body__left"]}>
            {!!data.email && (
              <span style={{ color: "black", fontWeight: "400" }}>
                Email:{" "}
                <a
                  style={{ color: "black", textDecoration: "underline" }}
                  href={`mailto:${data.email}`}
                  dangerouslySetInnerHTML={{ __html: data.email }}
                />
              </span>
            )}
            {!!data.position && (
              <div className={styles["faculty-card__body--item"]}>
                <p className={styles["faculty-card__body--item--title"]}>
                  Position
                </p>
                {hasFtsHtml(data.position) ? (
                  <p
                    className={styles["faculty-card__body--item--value"]}
                    dangerouslySetInnerHTML={{ __html: data.position }}
                  />
                ) : (
                  <p className={styles["faculty-card__body--item--value"]}>
                    {data.position}
                  </p>
                )}
              </div>
            )}
            {!!snippetKey && (
              <Typography
                variant='body2'
                component='span'
                sx={{
                  fontWeight: "300",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span
                  className='dot-2'
                  dangerouslySetInnerHTML={{
                    __html: data.snippet?.[snippetKey]?.value,
                  }}
                />
                <IconButton
                  size='small'
                  href={data.snippet?.[snippetKey]?.source}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='open in new tab'
                >
                  <LinkOutlined color='info' />
                </IconButton>
              </Typography>
            )}
            {!!data.institution?.name && (
              <div className={styles["faculty-card__body--item"]}>
                <p className={styles["faculty-card__body--item--title"]}>
                  Institution
                </p>
                <LinkWrapper
                  link={`/explore/institution/${data.institution.explore_slug || data.institution.id}`}
                  component={
                    hasFtsHtml(data.institution.name) ? (
                      <p
                        className={classNames(
                          styles["faculty-card__body--item--value"],
                          styles["link"]
                        )}
                        dangerouslySetInnerHTML={{ __html: data.institution.name }}
                      />
                    ) : (
                      <p
                        className={classNames(
                          styles["faculty-card__body--item--value"],
                          styles["link"]
                        )}
                      >
                        {data.institution.name}
                      </p>
                    )
                  }
                />
              </div>
            )}
            {data.experience_metadata &&
              !!data.experience_metadata.length && (
                <div className={styles["faculty-card__body--item"]}>
                  <p className={styles["faculty-card__body--item--title"]}>
                    Portfolio
                  </p>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: 20,
                      listStyleType: "disc",
                    }}
                  >
                    {data.experience_metadata
                      .slice(
                        0,
                        useSeeAll && !seeAll
                          ? 8
                          : data.experience_metadata.length
                      )
                      .map((item, index) => (
                        <li key={index} style={{ marginBottom: 4 }}>
                          <span
                            className={
                              styles["faculty-card__body--item--value"]
                            }
                            style={{ display: "inline" }}
                            dangerouslySetInnerHTML={{
                              __html: item.additional_info,
                            }}
                          />
                          {!!item.source_url && (
                            <IconButton
                              size='small'
                              href={item.source_url}
                              target='_blank'
                              rel='noopener noreferrer'
                              aria-label='open in new tab'
                            >
                              <LinkOutlined color='info' />
                            </IconButton>
                          )}
                        </li>
                      ))}
                  </ul>
                  {useSeeAll && (
                    <Button onClick={() => setSeeAll(!seeAll)}>
                      {seeAll ? "See less" : "See more"}
                    </Button>
                  )}
                </div>
              )}
          </div>

          {/* <div className={styles["faculty-card__body__right"]}>
            <Grid container className={styles["statistics"]} spacing="24px">
              {statistics.map((e, i) => (
                <Grid item xs={6} className={styles["statistic"]} key={i}>
                  {e.icon}
                  <div className={styles["faculty-card__body--item"]}>
                    <p className={styles["faculty-card__body--item--title"]}>
                      {e.title}
                    </p>
                    <p className={styles["faculty-card__body--item--value"]}>
                      {numberRegex(e.value)}
                    </p>
                  </div>
                </Grid>
              ))}
            </Grid>
          </div> */}
        </div>

        <div className={styles["faculty-card__bottom"]}>
          {data.keywords && data.keywords.length > 0 && (
            <div className={styles["keywords"]}>
              {(simpleMode ? data.keywords.slice(0, 2) : data.keywords).map(
                (item, index) =>
                  onKeywordClick ? (
                    <Button
                      size="small"
                      key={index}
                      className={classNames(
                        styles["keywords--item"],
                        styles["clickable"]
                      )}
                      variant="contained"
                      onClick={() => onKeywordClick(item.value)}
                    >
                      {item.rank && <b>#{item.rank}&nbsp;</b>}
                      {hasFtsHtml(item.value) ? (
                        <span dangerouslySetInnerHTML={{ __html: item.value }} />
                      ) : (
                        item.value
                      )}
                    </Button>
                  ) : (
                    <div className={styles["keywords--item"]} key={index}>
                      {item.rank && <b>#{item.rank}&nbsp;</b>}
                      {hasFtsHtml(item.value) ? (
                        <span dangerouslySetInnerHTML={{ __html: item.value }} />
                      ) : (
                        item.value
                      )}
                    </div>
                  )
              )}
              {simpleMode && data.keywords.length > 2 && (
                <Tag className={styles.keywordTag}>
                  +{data.keywords.length - 2}
                </Tag>
              )}
            </div>
          )}
          {!simpleMode && <FacultyCardBasicInfo data={data} />}
        </div>
      </div>
    </>
  );
};

const FacultyCard = ({ data, className, simpleMode, onKeywordClick, hideActions }) => {
  const data_list = useMemo(
    () => [data, ...(data?.duplicated_with_cross_institutions || [])],
    [data]
  );

  return (
    <Card
      data={data}
      className={className}
      simpleMode={simpleMode}
      onKeywordClick={onKeywordClick}
      hideActions={hideActions}
    />
  );
};

export default FacultyCard;
